import { useEffect } from "react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteError,
} from "@remix-run/react";
import type { LinksFunction } from "@remix-run/node";
import { Footer, GlobalLoading, Header } from "~/components";

import "./tailwind.css";
import { sendToDataLayer } from "~/utils/helpers";
import { CrossDomainTrackingSourceProvider } from "@tempt/ga4-cross-domain-tracking";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Manrope:wght@200..800&display=swap",
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { pathname } = location;

  // Send events to Data Layer
  useEffect(() => {
    if (pathname === "/") {
      sendToDataLayer({ event: "main_page_opened" });
    } else if (pathname === "/about") {
      sendToDataLayer({ event: "about_page_opened" });
    } else if (pathname.includes("/books/")) {
      const parts = pathname.split("/");
      const id = parts[parts.length - 1];
      sendToDataLayer({ event: "product_page_opened", property: id });
    }
  }, [pathname]);

  const withoutHeaderPages: string[] = [
    "/privacy_policy",
    "/terms_and_conditions",
    "/list_partners",
    "/cookie_policy",
    "do-not-sell-my-info",
  ];

  const showHeaderAndFooter = !withoutHeaderPages.some((page) =>
    pathname.includes(page),
  );

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon/favicon-16x16.png"
        />
        <link rel="manifest" href="/favicon/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/favicon/safari-pinned-tab.svg"
          color="#5bbad5"
        />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta
          name="msapplication-config"
          content="/favicon/browserconfig.xml"
        />
        <meta name="theme-color" content="#ffffff" />
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-W4Q31Z7PZ6"></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];

              function gtag() {
                  dataLayer.push(arguments);
              }
              window.gtag = gtag;

              gtag("js", new Date());

              gtag("config", "G-W4Q31Z7PZ6");
              gtag("get", "G-W4Q31Z7PZ6", "client_id", id => {console.log('client_id', id)});
              gtag("get", "G-W4Q31Z7PZ6", "session_id", id => {console.log('session_id', id)});
            `,
          }}
        />

        <Meta />
        <Links />
      </head>
      <body>
        <CrossDomainTrackingSourceProvider tagId="G-W4Q31Z7PZ6">
          <GlobalLoading />
          {showHeaderAndFooter && <Header />}
          <main>{children}</main>
          {showHeaderAndFooter && <Footer />}
          <ScrollRestoration />
          <Scripts />
        </CrossDomainTrackingSourceProvider>
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

export default function App() {
  return <Outlet />;
}
